import { Provider } from '@ai-sdk-tools/store';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import type { Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';
import type { ChatAppType } from '../../../queries/chats';
import { DISABLED_SETTINGS_ADMINISTRATION } from '../components/skill-administration/types';
import { ChatOverlayProvider, type ChatOverlayContextValue } from '../contexts/chat-overlay-context';
import type { ChatNotificationController } from '../hooks/use-chat-notification-controller';
import { DURABLE_NOVA_CHAT_COMPLETIONS_ENDPOINT } from '../lib/transport';

export { NovaProvider, useNovaWrapperState, type NovaProviderProps, type NovaProviderCallbacks } from './nova-provider';
interface NovaContextValue {
  userUuid: string;
  accountUuid?: string;
  /** Enrollment class UUID from the URL (e.g. employee web portal `classUuid` query); forwarded in send payloads when set. */
  classUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint: string;
  baseUrl?: string;
}

export type { NovaContextValue };

const NovaContext = createContext<NovaContextValue | undefined>(undefined);

interface NovaChatProviderProps {
  children: ReactNode;
  userUuid: string;
  accountUuid?: string;
  classUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint?: string;
  baseUrl?: string;
  initialMessages?: UIMessageWithMetadata[];
}

/**
 * Combined provider that wraps both ChatProvider (from @ai-sdk-tools/store)
 * and NovaContextProvider. This is the main entry point for the chat system.
 *
 * Simplified to merge both providers into one to reduce nesting.
 *
 * @example
 * ```tsx
 * <NovaChatProvider
 *   authProvider={authProvider}
 *   userUuid={userUuid}
 *   accountUuid={accountUuid}
 * >
 *   <ChatRoot chatUuid={chatUuid}>
 *     <Messages />
 *     <ChatInput />
 *   </ChatRoot>
 * </NovaChatProvider>
 * ```
 */
export function NovaChatProvider({
  children,
  userUuid,
  accountUuid,
  classUuid,
  accountName,
  scopes,
  authProvider,
  apiEndpoint = DURABLE_NOVA_CHAT_COMPLETIONS_ENDPOINT,
  baseUrl,
  initialMessages,
}: NovaChatProviderProps) {
  const novaContextValue = useMemo<NovaContextValue>(
    () => ({
      userUuid,
      accountUuid,
      classUuid,
      accountName,
      scopes,
      authProvider,
      apiEndpoint,
      baseUrl,
    }),
    [userUuid, accountUuid, classUuid, accountName, scopes, authProvider, apiEndpoint, baseUrl],
  );

  return (
    <Provider initialMessages={initialMessages}>
      <NovaContext.Provider value={novaContextValue}>{children}</NovaContext.Provider>
    </Provider>
  );
}

const disabledNotificationController: ChatNotificationController = {
  hasNotifications: false,
  browserNotificationsEnabled: false,
  browserNotificationPermission: 'default',
  browserNotificationsSupported: false,
  enableBrowserNotifications: async () => false,
  disableBrowserNotifications: () => undefined,
  noteLocalSend: () => undefined,
  noteStreamFinish: () => undefined,
  refetchChats: () => undefined,
};

export interface NovaReadOnlyChatProviderProps {
  children: ReactNode;
  userUuid: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  chatUuid: string;
  accountUuid?: string;
  accountName?: string;
  appType?: ChatAppType;
  apiEndpoint?: string;
  baseUrl?: string;
  initialMessages: UIMessageWithMetadata[];
  isLoadingMessages?: boolean;
}

/**
 * Provides the chat contexts needed to render a persisted transcript without
 * mounting Nova's interactive queries, polling, reconnect, or notification
 * controllers. Debug and audit surfaces should use this provider for snapshots.
 */
export function NovaReadOnlyChatProvider({
  children,
  userUuid,
  scopes,
  authProvider,
  chatUuid,
  accountUuid,
  accountName,
  appType,
  apiEndpoint = DURABLE_NOVA_CHAT_COMPLETIONS_ENDPOINT,
  baseUrl,
  initialMessages,
  isLoadingMessages = false,
}: NovaReadOnlyChatProviderProps) {
  const settingsCloseIntentRef = useRef<(() => void) | null>(null);
  const overlayContextValue = useMemo<ChatOverlayContextValue>(
    () => ({
      isSidebarOpen: false,
      setIsSidebarOpen: () => undefined,
      isFullscreen: false,
      setIsFullscreen: () => undefined,
      accountUuid,
      accountName,
      selectedAccount: undefined,
      setAccountUuid: () => undefined,
      onSelectAccount: async () => undefined,
      canSelectAccount: false,
      canConnectOutlook: false,
      isWtwColleague: false,
      chatUuid,
      isNewChat: false,
      isReadOnly: true,
      hasAnyMessages: initialMessages.length > 0,
      setHasAnyMessages: () => undefined,
      startNewChat: () => undefined,
      startNewChatWithInput: () => undefined,
      setActiveChat: () => undefined,
      openChatAtMessage: () => undefined,
      clearMessageNavigationTarget: () => undefined,
      isStandaloneMode: true,
      isEmbedded: false,
      isVisible: true,
      userUuid,
      userScopes: scopes,
      authProvider,
      apiEndpoint,
      appType,
      effectiveAppType: appType,
      currentChatAppType: appType,
      displayAccount: accountUuid ? { uuid: accountUuid, name: accountName ?? 'Unknown account' } : undefined,
      placeholder: '',
      initialMessages,
      isLoadingMessages,
      canResumeActiveStream: false,
      chatHasActiveStream: false,
      chatActiveStreamId: null,
      isActiveChat: (targetChatUuid) => targetChatUuid === chatUuid,
      beginRegeneration: () => undefined,
      clearPendingRegeneration: () => undefined,
      refetchMessages: async () => initialMessages,
      setChatError: () => undefined,
      isRetryingLastRequest: false,
      setRetryLastRequest: () => undefined,
      hasNotifications: false,
      additionalContext: {},
      setAdditionalContext: () => undefined,
      queuedNewChatInput: null,
      clearQueuedNewChatInput: () => undefined,
      notificationController: disabledNotificationController,
      settingsAdministration: DISABLED_SETTINGS_ADMINISTRATION,
      isSettingsOpen: false,
      setIsSettingsOpen: () => undefined,
      settingsSection: 'general',
      setSettingsSection: () => undefined,
      settingsBackSignal: 0,
      requestSettingsBack: () => undefined,
      requestSettingsClose: () => undefined,
      settingsCloseIntentRef,
      hasActiveSettingsSubView: false,
      setHasActiveSettingsSubView: () => undefined,
      isPublicMode: false,
      hasSavedOutlookConnection: false,
      outlookConnectEnabled: false,
      outlookNeedsReauth: false,
      reconnectOutlook: async () => false,
      setOutlookConnectEnabled: () => undefined,
      disconnectOutlook: async () => undefined,
      serverSettings: null,
      persistSetting: () => undefined,
      persistSettings: () => undefined,
    }),
    [
      accountName,
      accountUuid,
      apiEndpoint,
      appType,
      authProvider,
      chatUuid,
      initialMessages,
      isLoadingMessages,
      scopes,
      userUuid,
    ],
  );

  return (
    <NovaChatProvider
      userUuid={userUuid}
      accountUuid={accountUuid}
      accountName={accountName}
      scopes={scopes}
      authProvider={authProvider}
      apiEndpoint={apiEndpoint}
      baseUrl={baseUrl}
      initialMessages={initialMessages}
    >
      <ChatOverlayProvider value={overlayContextValue}>{children}</ChatOverlayProvider>
    </NovaChatProvider>
  );
}

/**
 * Hook to access Nova context values.
 * @throws Error if used outside of NovaChatProvider
 */
export function useNovaContext(): NovaContextValue {
  const context = useContext(NovaContext);
  if (!context) {
    throw new Error('useNovaContext must be used within NovaChatProvider');
  }
  return context;
}

export function useNovaContextSafe(): NovaContextValue | undefined {
  return useContext(NovaContext);
}

export type { NovaChatProviderProps };
